function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AccessibilityInfo, Keyboard, Platform } from "react-native";
import { isCaptionServiceEnabledAsync } from "@oui/native-utils";
import { useAppState } from "../hooks/useAppState";
var AccessibilityContext = /*#__PURE__*/ createContext({
    /**
   * Whether or not the OS captioning service is enabled on the device.
   */ isCaptionServiceEnabled: false,
    /**
   * Whether or not the keyboard is currently showing.
   */ isKeyboardShowing: false,
    /**
   * Whether or not a screen reader is enabled on the device.
   */ isScreenReaderEnabled: false,
    /**
   * Whether or not the OS is actively announcing a message.
   * @platform iOS
   */ isAnnouncingIOS: false,
    /**
   * Post a string to be announced by the screen reader.
   */ announceForAccessibility: function(announcement) {}
});
/**
 * @nosync
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-21}
 */ export function useAccessibilityContext() {
    return useContext(AccessibilityContext);
}
/**
 * Provides accessibility information to react children via
 * `AccessibilityProvider`.
 *
 * **Context Values:**
 *
 * ```
 *   &#47;**
 *    * Whether or not the OS captioning service is enabled on the device.
 *    *&#47;
 *   isCaptionServiceEnabled: false,
 *   &#47;**
 *    * Whether or not the keyboard is currently showing.
 *    *&#47;
 *   isKeyboardShowing: false,
 *   &#47;**
 *    * Whether or not a screen reader is enabled on the device.
 *    *&#47;
 *   isScreenReaderEnabled: false,
 *   &#47;**
 *    * Whether or not the OS is actively announcing a message.
 *    * &#64;platform iOS
 *    *&#47;
 *   isAnnouncingIOS: false,
 *   &#47;**
 *    * Post a string to be announced by the screen reader.
 *    *&#47;
 *   announceForAccessibility: (announcement: string) => {},
 * ```
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-21}
 */ export function AccessibilityProvider(props) {
    var _useState = _sliced_to_array(useState(false), 2), isScreenReaderEnabled = _useState[0], setIsScreenReaderEnabled = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), isKeyboardShowing = _useState1[0], setIsKeyboardShowing = _useState1[1];
    var _useState2 = _sliced_to_array(useState(false), 2), isCaptionServiceEnabled = _useState2[0], setIsCaptionServiceEnabled = _useState2[1];
    var _useState3 = _sliced_to_array(useState(false), 2), isAnnouncingIOS = _useState3[0], setIsAnnouncingIOS = _useState3[1];
    // iOS interrupts when calling announceForAccessibility (vs android which queues announcments
    // to be read one after the other. To workaround, we set state when we are announcing and wait for
    // it to finish before sending more announcements to the system
    var announceForAccessibility = useCallback(function(announcement) {
        if (Platform.OS === 'ios' && isScreenReaderEnabled) {
            setIsAnnouncingIOS(true);
        }
        AccessibilityInfo.announceForAccessibility(announcement);
    }, [
        isScreenReaderEnabled
    ]);
    useEffect(function() {
        var didShow = function didShow() {
            setIsKeyboardShowing(true);
        };
        var didHide = function didHide() {
            setIsKeyboardShowing(false);
        };
        void AccessibilityInfo.isScreenReaderEnabled().then(function(enabled) {
            setIsScreenReaderEnabled(enabled && Platform.OS !== 'web');
        });
        var screenReaderListener = AccessibilityInfo.addEventListener('screenReaderChanged', setIsScreenReaderEnabled);
        var announcementListener = AccessibilityInfo.addEventListener('announcementFinished', function() {
            setIsAnnouncingIOS(false);
        });
        void isCaptionServiceEnabledAsync().then(setIsCaptionServiceEnabled);
        var showListener = Keyboard.addListener('keyboardDidShow', didShow);
        var hideListener = Keyboard.addListener('keyboardDidHide', didHide);
        return function() {
            screenReaderListener.remove();
            showListener.remove();
            hideListener.remove();
            announcementListener.remove();
        };
    }, []);
    useAppState(function(appState) {
        if (appState === 'active') {
            void isCaptionServiceEnabledAsync().then(setIsCaptionServiceEnabled);
        }
    });
    var value = useMemo(function() {
        return {
            isCaptionServiceEnabled: isCaptionServiceEnabled,
            isKeyboardShowing: isKeyboardShowing,
            isScreenReaderEnabled: isScreenReaderEnabled,
            isAnnouncingIOS: isAnnouncingIOS,
            announceForAccessibility: announceForAccessibility
        };
    }, [
        isCaptionServiceEnabled,
        isKeyboardShowing,
        isScreenReaderEnabled,
        isAnnouncingIOS,
        announceForAccessibility
    ]);
    return /*#__PURE__*/ _jsx(AccessibilityContext.Provider, {
        value: value,
        children: props.children
    });
}
